import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  loadElectricalSafetyExcelTemplateWorkbook,
  populateElectricalSafetyExcelWorksheet,
} from "components/project/ElectricalSafetyExcelDownloadBtn";
import {
  loadUdExcelTemplateWorkbook,
  populateUdImageExcelWorksheet,
} from "components/project/UdExcelDownloadBtn";
import {
  loadUdVideoExcelTemplateWorkbook,
  populateUdVideoExcelWorksheet,
} from "components/project/VideoExcelDownloadBtn";

const PROJECT_UD1000_COVER_TEMPLATE_URL = `${
  process.env.PUBLIC_URL || ""
}/templates/excel_cover_template.xlsx`;

const UD1000_COVER_TEMPLATE_SHEET_NAMES = {
  intro: "표지",
  overview: "용역 개요",
  index: "융합 점검 목록 및 결과",
};

const UD1000_COVER_TEMPLATE_FALLBACK_INDEXES = {
  intro: 0,
  overview: 1,
  index: 2,
};

const UD1000_INDEX_ROWS_PER_EXCEL_SHEET = 15;

function cloneExcelValue(value) {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
}

function translateExcel(t, key, defaultValue, options = {}) {
  if (typeof t === "function") {
    return t(key, {
      defaultValue,
      ...options,
    });
  }

  return defaultValue;
}

function findWorkbookImageModel(workbook, imageId) {
  const media = workbook?.model?.media || [];

  return (
    media.find((item) => item?.index === imageId) ||
    media[imageId] ||
    null
  );
}

function cloneImageAnchor(anchor) {
  if (!anchor) return undefined;

  if (anchor.model) {
    return cloneExcelValue(anchor.model);
  }

  if (anchor.nativeCol !== undefined) {
    return {
      nativeCol: anchor.nativeCol,
      nativeColOff: anchor.nativeColOff || 0,
      nativeRow: anchor.nativeRow,
      nativeRowOff: anchor.nativeRowOff || 0,
    };
  }

  return {
    col: anchor.col,
    row: anchor.row,
  };
}

function cloneImageRange(range) {
  if (!range) return undefined;

  const clonedRange = {
    tl: cloneImageAnchor(range.tl),
    br: cloneImageAnchor(range.br),
  };

  if (range.ext) {
    clonedRange.ext = cloneExcelValue(range.ext);
    delete clonedRange.br;
  }

  if (range.editAs) {
    clonedRange.editAs = range.editAs;
  }

  if (range.hyperlinks) {
    clonedRange.hyperlinks = cloneExcelValue(range.hyperlinks);
  }

  return clonedRange;
}

function copyTemplateWorksheetImages({
  sourceWorkbook,
  sourceSheet,
  targetWorkbook,
  targetSheet,
}) {
  if (
    !sourceWorkbook ||
    !sourceSheet ||
    !targetWorkbook ||
    !targetSheet ||
    typeof sourceSheet.getImages !== "function"
  ) {
    return;
  }

  sourceSheet.getImages().forEach((image) => {
    const media = findWorkbookImageModel(sourceWorkbook, image.imageId);
    const extension = media?.extension || "png";
    const imageConfig = media?.buffer
      ? { buffer: media.buffer, extension }
      : media?.base64
        ? { base64: media.base64, extension }
        : null;
    const range = cloneImageRange(image.range);

    if (!imageConfig || !range) return;

    try {
      const copiedImageId = targetWorkbook.addImage(imageConfig);
      targetSheet.addImage(copiedImageId, range);
    } catch (_error) {
      // Keep the worksheet exportable even if a template image cannot be cloned.
    }
  });
}

function copyTemplateWorksheet(templateSheet, targetSheet, options = {}) {
  if (!templateSheet || !targetSheet) return;

  const {
    sourceWorkbook = templateSheet.workbook || templateSheet._workbook,
    targetWorkbook = targetSheet.workbook || targetSheet._workbook,
    copyImages = false,
  } = options;

  targetSheet.properties = cloneExcelValue(templateSheet.properties) || {};
  targetSheet.pageSetup = cloneExcelValue(templateSheet.pageSetup) || {};
  targetSheet.headerFooter = cloneExcelValue(templateSheet.headerFooter) || {};
  targetSheet.views = cloneExcelValue(templateSheet.views) || [];
  targetSheet.state = templateSheet.state;
  targetSheet.autoFilter = cloneExcelValue(templateSheet.autoFilter) || undefined;

  templateSheet.columns.forEach((column, index) => {
    const targetColumn = targetSheet.getColumn(index + 1);
    targetColumn.width = column.width;
    targetColumn.hidden = column.hidden;
    targetColumn.outlineLevel = column.outlineLevel;
    targetColumn.style = cloneExcelValue(column.style) || {};
  });

  (templateSheet.model?.merges || []).forEach((range) => {
    targetSheet.mergeCells(range);
  });

  for (let rowNumber = 1; rowNumber <= templateSheet.rowCount; rowNumber += 1) {
    const sourceRow = templateSheet.getRow(rowNumber);
    const targetRow = targetSheet.getRow(rowNumber);

    targetRow.height = sourceRow.height;
    targetRow.hidden = sourceRow.hidden;
    targetRow.outlineLevel = sourceRow.outlineLevel;
    targetRow.style = cloneExcelValue(sourceRow.style) || {};

    for (
      let columnNumber = 1;
      columnNumber <= templateSheet.columnCount;
      columnNumber += 1
    ) {
      const sourceCell = sourceRow.getCell(columnNumber);
      const targetCell = targetRow.getCell(columnNumber);

      targetCell.value = cloneExcelValue(sourceCell.value);
      targetCell.style = cloneExcelValue(sourceCell.style) || {};
      targetCell.numFmt = sourceCell.numFmt;
      targetCell.alignment = cloneExcelValue(sourceCell.alignment) || undefined;
      targetCell.font = cloneExcelValue(sourceCell.font) || undefined;
      targetCell.fill = cloneExcelValue(sourceCell.fill) || undefined;
      targetCell.border = cloneExcelValue(sourceCell.border) || undefined;
      targetCell.protection =
        cloneExcelValue(sourceCell.protection) || undefined;
    }
  }

  if (copyImages) {
    copyTemplateWorksheetImages({
      sourceWorkbook,
      sourceSheet: templateSheet,
      targetWorkbook,
      targetSheet,
    });
  }
}

function reapplyTemplateWorksheetStyles(templateSheet, targetSheet) {
  if (!templateSheet || !targetSheet) return;

  templateSheet.columns.forEach((column, index) => {
    const targetColumn = targetSheet.getColumn(index + 1);
    targetColumn.style = cloneExcelValue(column.style) || {};
  });

  for (let rowNumber = 1; rowNumber <= templateSheet.rowCount; rowNumber += 1) {
    const sourceRow = templateSheet.getRow(rowNumber);
    const targetRow = targetSheet.getRow(rowNumber);
    targetRow.style = cloneExcelValue(sourceRow.style) || {};

    for (
      let columnNumber = 1;
      columnNumber <= templateSheet.columnCount;
      columnNumber += 1
    ) {
      const sourceCell = sourceRow.getCell(columnNumber);
      const targetCell = targetRow.getCell(columnNumber);

      targetCell.style = cloneExcelValue(sourceCell.style) || {};
      targetCell.numFmt = sourceCell.numFmt;
      targetCell.alignment = cloneExcelValue(sourceCell.alignment) || undefined;
      targetCell.font = cloneExcelValue(sourceCell.font) || undefined;
      targetCell.fill = cloneExcelValue(sourceCell.fill) || undefined;
      targetCell.border = cloneExcelValue(sourceCell.border) || undefined;
      targetCell.protection =
        cloneExcelValue(sourceCell.protection) || undefined;
    }
  }
}

function copyWorksheetLayout(templateSheet, targetSheet) {
  if (!templateSheet || !targetSheet) return;

  targetSheet.properties = cloneExcelValue(templateSheet.properties) || {};
  targetSheet.pageSetup = cloneExcelValue(templateSheet.pageSetup) || {};
  targetSheet.headerFooter = cloneExcelValue(templateSheet.headerFooter) || {};
  targetSheet.views = cloneExcelValue(templateSheet.views) || [];
  targetSheet.state = templateSheet.state;

  templateSheet.columns.forEach((column, index) => {
    const targetColumn = targetSheet.getColumn(index + 1);
    targetColumn.width = column.width;
    targetColumn.hidden = column.hidden;
    targetColumn.outlineLevel = column.outlineLevel;
  });

  for (let rowNumber = 1; rowNumber <= templateSheet.rowCount; rowNumber += 1) {
    const sourceRow = templateSheet.getRow(rowNumber);
    const targetRow = targetSheet.getRow(rowNumber);

    targetRow.height = sourceRow.height;
    targetRow.hidden = sourceRow.hidden;
    targetRow.outlineLevel = sourceRow.outlineLevel;
  }
}

function sanitizeWorksheetName(value, fallback) {
  const safeValue = String(value || "")
    .replace(/[[\]\\/*?:]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return (safeValue || fallback || "Sheet").slice(0, 31);
}

function createWorksheetName(page, index, usedNames) {
  const baseName = sanitizeWorksheetName(
    page?.title || page?.file?.clean_file_name || page?.file?.file_name,
    `Sheet ${index + 1}`,
  );

  if (!usedNames.has(baseName)) {
    usedNames.add(baseName);
    return baseName;
  }

  let duplicateIndex = 2;
  while (duplicateIndex < 9999) {
    const suffix = ` (${duplicateIndex})`;
    const nextName = `${baseName.slice(0, Math.max(31 - suffix.length, 1)).trimEnd()}${suffix}`;

    if (!usedNames.has(nextName)) {
      usedNames.add(nextName);
      return nextName;
    }

    duplicateIndex += 1;
  }

  const fallbackName = `Sheet ${index + 1}`;
  usedNames.add(fallbackName);
  return fallbackName;
}

function applyProjectWorksheetPageSetup(sheet, pageNumber) {
  if (!sheet) return;

  sheet.pageSetup = {
    ...sheet.pageSetup,
    firstPageNumber: pageNumber,
  };
}

function emitExportProgress(onProgress, payload) {
  if (typeof onProgress !== "function") return;
  onProgress(payload);
}

function getWorksheetProgressPercent(current, total) {
  if (!total) return 18;
  return 18 + Math.round((current / total) * 64);
}

function pauseForPaint() {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });
}

function getUd1000ExcelImages(file = {}) {
  if (file?.file_type === "video") {
    return {
      graph_image_1: file?.graph_image_1 || "",
      graph_image_2: file?.graph_image_2 || "",
      frame_image_1: file?.frame_image_1 || "",
      frame_image_2: file?.frame_image_2 || "",
      waveform_image_1: file?.waveform_image_1 || "",
      waveform_image_2: file?.waveform_image_2 || "",
    };
  }

  return {
    image_visual: file?.image_visual || file?.markup_image_visual || "",
    markup_image_graph: file?.markup_image_graph || "",
    image_spectrogram: file?.image_spectrogram || "",
    markup_image_thermal:
      file?.markup_image_thermal || file?.image_thermal || "",
    markup_image_triplespot: file?.markup_image_triplespot || "",
    image_uv: file?.image_uv || "",
  };
}

function isDataUrl(value) {
  return typeof value === "string" && value.trim().startsWith("data:");
}

function isFetchableImageSource(value) {
  if (typeof value !== "string") return false;

  const trimmed = value.trim();
  return (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("/")
  );
}

async function normalizeExcelImageSource(value) {
  if (!value || typeof value !== "string") return "";

  const trimmed = value.trim();
  if (!trimmed || isDataUrl(trimmed)) return trimmed;

  if (!isFetchableImageSource(trimmed)) {
    return trimmed;
  }

  try {
    return await loadImageAsDataUrl(trimmed);
  } catch (error) {
    console.warn("[ProjectReportUdExcelExport] image conversion failed:", {
      src: trimmed,
      message: error?.message,
    });
    return "";
  }
}

async function normalizeExcelImages(images = {}) {
  const entries = await Promise.all(
    Object.entries(images).map(async ([key, value]) => [
      key,
      await normalizeExcelImageSource(value),
    ]),
  );

  return Object.fromEntries(entries);
}

function getElectricalSafetyExcelImages(file = {}) {
  return {
    image_visual: file?.image_visual || "",
    markup_image_thermal: file?.markup_image_thermal || "",
    markup_image_triplespot:
      file?.markup_image_triplespot || file?.markup_image_thermal || "",
  };
}

function setCellValue(sheet, address, value) {
  sheet.getCell(address).value = value ?? "";
}

function mergeCell(sheet, range) {
  try {
    sheet.mergeCells(range);
  } catch (_error) {
    // Ignore duplicate merge attempts when regenerating the same layout.
  }
}

function mergeCellAlignment(sheet, address, alignment) {
  const cell = sheet.getCell(address);
  cell.alignment = {
    ...(cell.alignment ?? {}),
    ...alignment,
  };
}

function mergeCellFont(sheet, address, font) {
  const cell = sheet.getCell(address);
  cell.font = {
    ...(cell.font ?? {}),
    ...font,
  };
}

function setCellFill(sheet, address, fill) {
  sheet.getCell(address).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: fill },
  };
}

function setCellBorder(sheet, address, border) {
  sheet.getCell(address).border = border;
}

function applyRangeBorder(sheet, startRow, endRow, startCol, endCol, border) {
  for (let row = startRow; row <= endRow; row += 1) {
    for (let col = startCol; col <= endCol; col += 1) {
      setCellBorder(sheet, sheet.getRow(row).getCell(col).address, border);
    }
  }
}

function applyRangeFill(sheet, startRow, endRow, startCol, endCol, fill) {
  for (let row = startRow; row <= endRow; row += 1) {
    for (let col = startCol; col <= endCol; col += 1) {
      setCellFill(sheet, sheet.getRow(row).getCell(col).address, fill);
    }
  }
}

function padIndexRows(rows = [], rowCount = 15) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const baseOrder = safeRows[0]?.order ?? 1;

  return Array.from({ length: rowCount }, (_, index) => {
    const row = safeRows[index];

    if (row) {
      return row;
    }

    return {
      id: `ud1000-cover-index-empty-${baseOrder + index}`,
      order: baseOrder + index,
      facilityName: "",
      inspectionStatus: "",
      inspectionResult: "",
      pageNumber: "",
    };
  });
}

function chunkRows(rows = [], chunkSize = UD1000_INDEX_ROWS_PER_EXCEL_SHEET) {
  if (!Array.isArray(rows) || !rows.length) return [[]];

  const chunks = [];
  for (let index = 0; index < rows.length; index += chunkSize) {
    chunks.push(rows.slice(index, index + chunkSize));
  }

  return chunks;
}

function expandUd1000ExcelCoverPages(coverPages = [], t) {
  if (!Array.isArray(coverPages)) return [];

  return coverPages.flatMap((page) => {
    if (page?.coverPageType !== "index") {
      return [page];
    }

    const rows = Array.isArray(page?.rows) ? page.rows : [];

    if (rows.length <= UD1000_INDEX_ROWS_PER_EXCEL_SHEET) {
      return [page];
    }

    return chunkRows(rows).map((chunk, index) => ({
      ...page,
      id: `${page?.id || "ud1000-cover-index"}-${index + 1}`,
      title:
        index === 0
          ? page?.title
          : translateExcel(
              t,
              "projectDetail.projectReportEditor.ud1000Cover.pageTitles.indexWithPage",
              "융합 점검 목록 및 결과 {{page}}",
              {
                page: index + 1,
              },
            ),
      showIndexCaption: index === 0 && page?.showIndexCaption !== false,
      rows: chunk,
    }));
  });
}

function clearWorksheetValues(
  sheet,
  startRow = 1,
  endRow = 30,
  startCol = 1,
  endCol = 8,
) {
  for (let row = startRow; row <= endRow; row += 1) {
    for (let col = startCol; col <= endCol; col += 1) {
      const cell = sheet.getRow(row).getCell(col);
      cell.value = "";
      cell.fill = undefined;
      cell.border = undefined;
      cell.font = undefined;
      cell.alignment = undefined;
    }
  }
}

async function loadImageAsDataUrl(url) {
  const response = await fetch(url, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`image fetch failed: ${response.status}`);
  }

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () =>
      resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function loadProjectUd1000CoverExcelTemplateWorkbook() {
  const response = await fetch(PROJECT_UD1000_COVER_TEMPLATE_URL);

  if (!response.ok) {
    throw new Error(`project cover template fetch failed: ${response.status}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  return workbook;
}

function addBase64Image(workbook, sheet, base64, range) {
  if (!base64 || typeof base64 !== "string") return;

  const imageId = workbook.addImage({
    base64,
    extension: "png",
  });

  sheet.addImage(imageId, range);
}
function hwpToPt(hwp) {
  return Math.round((hwp / 100) * 2.8346 * 10) / 10;
}
const COVER_BORDER = {
  top: { style: "thin", color: { argb: "000000" } },
  left: { style: "thin", color: { argb: "000000" } },
  bottom: { style: "thin", color: { argb: "000000" } },
  right: { style: "thin", color: { argb: "000000" } },
};

// ────────────────────────────────────────────────
// 1. initializeCoverWorksheet  (기존 함수 교체)
// ────────────────────────────────────────────────
function initializeCoverWorksheet(sheet, templateSheet, pageNumber) {
  // 실제 보고서 시트의 컬럼 너비·행높이·pageSetup 을 그대로 복사
  copyWorksheetLayout(templateSheet, sheet);

  // 값/스타일 초기화 (표지는 최대 20행 사용)
  clearWorksheetValues(sheet, 1, 20, 1, 8);

  // pageSetup: 실제 보고서 시트와 완전히 동일하게
  sheet.pageSetup = {
    ...sheet.pageSetup,
    firstPageNumber: pageNumber,
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 1,
    horizontalCentered: false,
    verticalCentered: false,
    paperSize: 9, // A4
    orientation: "portrait",
    margins: {
      left: 0.63, // 6000 HWP → 60mm → 2.36in  ※ Excel margin 단위: inch
      right: 0.595, // 5669 HWP → 56.69mm → 2.23in
      top: 0.893, // 8503 HWP → 85.03mm → 3.35in  ← 실제 보고서와 동일하게
      bottom: 0.744, // 7086 HWP → 70.86mm → 2.79in
      header: 0,
      footer: 0,
    },
  };
}

// ────────────────────────────────────────────────
// 2. renderUd1000IntroCoverSheet  (기존 함수 교체)
// HML Section0 구조 재현:
//   P[0]  : {{project_name}}       CharShape=22 (20pt Bold)
//   P[1~3]: 빈줄 (간격)
//   P[4]  : 융복합 안전점검 결과보고서  CharShape=19 (30pt)
//   P[5~11]: 빈줄
//   P[12] : {{period_end_year_month}} CharShape=22 (20pt Bold)
//   P[13~22]: 빈줄
//   P[23] : {{contractor}}           CharShape=34 (21pt)
//
// 24개 P를 A4 가용높이에 균등 배분 → 각 행 약 81pt
// 로고는 중앙(행7~11) 이미지로 삽입
// ────────────────────────────────────────────────
function renderUd1000IntroCoverSheet({ workbook, sheet, page, logoBase64, t }) {
  // 24행 균등 높이 (HML: 전체 가용 68597 HWP / 24 ≈ 2858 HWP = 81pt)
  const ROW_HEIGHT = 81;
  for (let i = 1; i <= 24; i += 1) {
    sheet.getRow(i).height = ROW_HEIGHT;
  }

  // Row 1: 프로젝트명
  mergeCell(sheet, "A1:H1");
  setCellValue(sheet, "A1", page?.projectName ?? "");
  mergeCellAlignment(sheet, "A1", {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  });
  mergeCellFont(sheet, "A1", {
    size: 20,
    bold: true,
    color: { argb: "FF000000" },
  });

  // Row 5: 보고서 제목
  mergeCell(sheet, "A5:H5");
  setCellValue(
    sheet,
    "A5",
    page?.headline ||
      translateExcel(
        t,
        "projectDetail.projectReportEditor.ud1000Cover.introHeadline",
        "융복합 안전점검 결과보고서",
      ),
  );
  mergeCellAlignment(sheet, "A5", { horizontal: "center", vertical: "middle" });
  mergeCellFont(sheet, "A5", {
    size: 30,
    bold: false,
    color: { argb: "FF000000" },
  });

  // Row 13: 년월
  mergeCell(sheet, "A13:H13");
  setCellValue(sheet, "A13", page?.periodEndYearMonth ?? "");
  mergeCellAlignment(sheet, "A13", {
    horizontal: "center",
    vertical: "middle",
  });
  mergeCellFont(sheet, "A13", {
    size: 20,
    bold: true,
    color: { argb: "FF000000" },
  });

  // Row 7~11: 로고 이미지 (중앙)
  if (logoBase64) {
    addBase64Image(workbook, sheet, logoBase64, {
      tl: { col: 2, row: 6 }, // C7
      br: { col: 6, row: 11 }, // G11
    });
  }

  // Row 24: 업체명
  mergeCell(sheet, "A24:H24");
  setCellValue(sheet, "A24", page?.contractor ?? "");
  mergeCellAlignment(sheet, "A24", {
    horizontal: "center",
    vertical: "middle",
  });
  mergeCellFont(sheet, "A24", {
    size: 21,
    bold: false,
    color: { argb: "FF000000" },
  });
}

// ────────────────────────────────────────────────
// 3. renderUd1000OverviewCoverSheet  (기존 함수 교체)
// HML Section1 구조 재현 (용역개요 부분만):
//
//   행 레이아웃 (HML P 순서 → Excel 행):
//   Row 1 : 용역명칭 (12pt Bold)
//   Row 2 : 용역일자 (12pt Bold)
//   Row 3 : 용역업체 (12pt Bold)
//   Row 4 : 참여인원 (12pt Bold)
//   Row 5 : [빈줄]
//   Row 6 : 진단목적 레이블 (12pt Bold)
//   Row 7~9 : purpose 내용 (테두리 박스, 높이 Table0=474.6pt → 3행으로)
//   Row 10 : [빈줄]
//   Row 11 : 진단결과 분석의견 레이블 (12pt Bold)
//   Row 12~15 : analysis_result 내용 (테두리 박스, 높이 Table1=739.3pt → 4행)
//   Row 16~19 : [빈줄 × 4] (융복합점검 목록 제목 앞 간격)
// ────────────────────────────────────────────────
function renderUd1000OverviewCoverSheet({ sheet, page, t }) {
  const purposeText = String(page?.purpose ?? "");
  const diagnosisText = String(
    page?.analysisResultText ||
      [page?.diagnosisResult, ...(page?.analysisOpinionLines ?? [])]
        .filter(Boolean)
        .join("\n"),
  );
  const participantText = String(
    page?.participantText ||
      [page?.participantCount, page?.participantNames]
        .filter(Boolean)
        .join(" "),
  );
  const periodText = [page?.periodStart, page?.periodEnd]
    .filter(Boolean)
    .join(" ~ ");
  const labels = getUd1000OverviewLabels(t);

  // 행 높이 설정 (HML 기반)
  // 용역 정보 4행: CharShape=23(12pt), ParaShape=35
  // lineSpacing=160% → 약 12*1.6 = 19.2pt → 여백 포함 약 28pt
  const INFO_ROW_H = 28;
  // purpose 테이블: 474.6pt → 3행 → 158.2pt each
  const PURPOSE_ROW_H = 158;
  // analysis 테이블: 739.3pt → 4행 → 184.8pt each
  const ANALYSIS_ROW_H = 185;
  // 빈줄 / 레이블 행
  const LABEL_ROW_H = 24;
  const EMPTY_ROW_H = 16;

  const heights = [
    INFO_ROW_H, // 1: 용역명칭
    INFO_ROW_H, // 2: 용역일자
    INFO_ROW_H, // 3: 용역업체
    INFO_ROW_H, // 4: 참여인원
    EMPTY_ROW_H, // 5: 빈줄
    LABEL_ROW_H, // 6: 진단목적 레이블
    PURPOSE_ROW_H, // 7
    PURPOSE_ROW_H, // 8
    PURPOSE_ROW_H, // 9
    EMPTY_ROW_H, // 10: 빈줄
    LABEL_ROW_H, // 11: 진단결과 레이블
    ANALYSIS_ROW_H, // 12
    ANALYSIS_ROW_H, // 13
    ANALYSIS_ROW_H, // 14
    ANALYSIS_ROW_H, // 15
    EMPTY_ROW_H, // 16
    EMPTY_ROW_H, // 17
    EMPTY_ROW_H, // 18
    EMPTY_ROW_H, // 19
    EMPTY_ROW_H, // 20
  ];
  heights.forEach((h, idx) => {
    sheet.getRow(idx + 1).height = h;
  });

  // ── 용역 정보 4행 ──
  const infoRows = [
    [1, labels.contractName, page?.contractName ?? ""],
    [2, labels.period, periodText],
    [3, labels.contractor, page?.contractor ?? ""],
    [4, labels.participants, participantText],
  ];

  infoRows.forEach(([rowNum, label, value]) => {
    // A:B = 레이블, C:H = 값
    mergeCell(sheet, `A${rowNum}:B${rowNum}`);
    mergeCell(sheet, `C${rowNum}:H${rowNum}`);
    setCellValue(sheet, `A${rowNum}`, label);
    setCellValue(sheet, `C${rowNum}`, value);

    mergeCellAlignment(sheet, `A${rowNum}`, {
      horizontal: "left",
      vertical: "middle",
    });
    mergeCellAlignment(sheet, `C${rowNum}`, {
      horizontal: "left",
      vertical: "middle",
      wrapText: true,
    });
    mergeCellFont(sheet, `A${rowNum}`, {
      size: 12,
      bold: true,
      color: { argb: "FF000000" },
    });
    mergeCellFont(sheet, `C${rowNum}`, {
      size: 12,
      bold: false,
      color: { argb: "FF000000" },
    });

    // 테두리 (thin)
    applyRangeBorder(sheet, rowNum, rowNum, 1, 8, COVER_BORDER);
  });

  // ── 진단목적 레이블 ──
  mergeCell(sheet, "A6:H6");
  setCellValue(sheet, "A6", labels.purpose);
  mergeCellFont(sheet, "A6", {
    size: 12,
    bold: true,
    color: { argb: "FF000000" },
  });
  mergeCellAlignment(sheet, "A6", { horizontal: "left", vertical: "middle" });

  // ── purpose 박스 (row 7~9) ──
  mergeCell(sheet, "A7:H9");
  setCellValue(sheet, "A7", purposeText);
  mergeCellAlignment(sheet, "A7", {
    horizontal: "left",
    vertical: "top",
    wrapText: true,
  });
  mergeCellFont(sheet, "A7", { size: 12, color: { argb: "FF000000" } });
  applyRangeBorder(sheet, 7, 9, 1, 8, COVER_BORDER);

  // ── 진단결과 분석의견 레이블 ──
  mergeCell(sheet, "A11:H11");
  setCellValue(sheet, "A11", labels.analysis);
  mergeCellFont(sheet, "A11", {
    size: 12,
    bold: true,
    color: { argb: "FF000000" },
  });
  mergeCellAlignment(sheet, "A11", { horizontal: "left", vertical: "middle" });

  // ── analysis_result 박스 (row 12~15) ──
  mergeCell(sheet, "A12:H15");
  setCellValue(sheet, "A12", diagnosisText);
  mergeCellAlignment(sheet, "A12", {
    horizontal: "left",
    vertical: "top",
    wrapText: true,
  });
  mergeCellFont(sheet, "A12", { size: 12, color: { argb: "FF000000" } });
  applyRangeBorder(sheet, 12, 15, 1, 8, COVER_BORDER);
}

// ────────────────────────────────────────────────
// 4. renderUd1000IndexCoverSheet  (기존 함수 교체)
// HML Table2 구조 재현:
//   - 5컬럼 → A, B:E(merge), F, G, H
//   - Header row: 98.9pt, 데이터 행: 79.3pt (짝수행 96pt)
//   - 헤더 배경: D9D9D9, 테두리: thin black
//   - 제목 "융복합 점검 목록 및 결과": 14pt Bold (row 1, 28pt)
//   - 인덱스 테이블: row 3(헤더) + row 4~18(데이터 15행)
// ────────────────────────────────────────────────
function renderUd1000IndexCoverSheet({ sheet, page, t }) {
  const rows = padIndexRows(page?.rows, UD1000_INDEX_ROWS_PER_EXCEL_SHEET);
  const labels = getUd1000IndexLabels(t);

  // 행 높이 (HML Table2 기반)
  sheet.getRow(1).height = hwpToPt(3488); // 제목행 ≈ 98.9pt → 28pt로 조정 (제목 전용)
  sheet.getRow(1).height = 28; // 제목 행은 컴팩트하게
  sheet.getRow(2).height = 6; // 간격
  sheet.getRow(3).height = hwpToPt(3488); // 헤더 행 98.9pt
  for (let r = 4; r <= 18; r += 1) {
    // 홀수 데이터 행(짝수 인덱스): 96pt, 나머지: 79.3pt
    // HML Row[02,04] = 3388 HWP = 96pt (시설명이 2줄인 행)
    // 단순하게 모두 동일 높이로 통일
    sheet.getRow(r).height = hwpToPt(2797); // 79.3pt
  }

  // ── 제목 ──
  mergeCell(sheet, "A1:H1");
  setCellValue(sheet, "A1", page?.title || labels.title);
  mergeCellAlignment(sheet, "A1", { horizontal: "center", vertical: "middle" });
  mergeCellFont(sheet, "A1", {
    size: 14,
    bold: true,
    underline: true,
    color: { argb: "FF000000" },
  });

  // ── 헤더 행 (row 3) ──
  // A=번호, B:E=설비명, F=점검여부, G=점검결과, H=페이지
  const headerCells = [
    ["A3", labels.order],
    ["B3:E3", labels.facilityName],
    ["F3", `${labels.inspection}\n${labels.status}`],
    ["G3", `${labels.inspection}\n${labels.result}`],
    ["H3", labels.pageNumber],
  ];
  headerCells.forEach(([range, value]) => {
    mergeCell(sheet, range);
    const anchor = range.split(":")[0];
    setCellValue(sheet, anchor, value);
    mergeCellAlignment(sheet, anchor, {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    });
    mergeCellFont(sheet, anchor, {
      size: 11,
      bold: true,
      color: { argb: "FF000000" },
    });
  });
  applyRangeFill(sheet, 3, 3, 1, 8, "D9D9D9");
  applyRangeBorder(sheet, 3, 18, 1, 8, COVER_BORDER);

  // ── 데이터 행 (row 4~18) ──
  rows.forEach((row, index) => {
    const rowNumber = 4 + index;
    mergeCell(sheet, `B${rowNumber}:E${rowNumber}`);

    setCellValue(sheet, `A${rowNumber}`, row.order ?? "");
    // 설비명: facilityName + partName
    const facilityDisplay = [row.facilityName, row.partName]
      .filter(Boolean)
      .join(" ");
    setCellValue(sheet, `B${rowNumber}`, facilityDisplay);
    setCellValue(sheet, `F${rowNumber}`, row.inspectionStatus ?? "");
    setCellValue(sheet, `G${rowNumber}`, row.inspectionResult ?? "");
    setCellValue(sheet, `H${rowNumber}`, row.pageNumber ?? "");

    // 중앙정렬 컬럼
    ["A", "F", "G", "H"].forEach((col) => {
      mergeCellAlignment(sheet, `${col}${rowNumber}`, {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      });
      mergeCellFont(sheet, `${col}${rowNumber}`, {
        size: 10,
        color: { argb: "FF000000" },
      });
    });

    // 설비명: 좌측정렬
    mergeCellAlignment(sheet, `B${rowNumber}`, {
      horizontal: "left",
      vertical: "middle",
      wrapText: true,
    });
    mergeCellFont(sheet, `B${rowNumber}`, {
      size: 10,
      color: { argb: "FF000000" },
    });
  });
}

function getUd1000CoverTemplateSheet(workbook, coverPageType) {
  if (!workbook || !coverPageType) return null;

  const sheetName = UD1000_COVER_TEMPLATE_SHEET_NAMES[coverPageType];
  const fallbackIndex = UD1000_COVER_TEMPLATE_FALLBACK_INDEXES[coverPageType];

  return (
    (sheetName && workbook.getWorksheet?.(sheetName)) ||
    workbook.worksheets?.[fallbackIndex] ||
    null
  );
}

function getOverviewPeriodText(page) {
  return [page?.periodStart, page?.periodEnd].filter(Boolean).join(" ~ ");
}

function getOverviewParticipantText(page) {
  return String(
    page?.participantText ||
      [page?.participantCount, page?.participantNames]
        .filter(Boolean)
        .join(" "),
  );
}

function getOverviewAnalysisText(page) {
  return String(
    page?.analysisResultText ||
      [page?.diagnosisResult, ...(page?.analysisOpinionLines ?? [])]
        .filter(Boolean)
        .join("\n"),
  );
}

function getExcelTextUnits(value) {
  return Array.from(String(value ?? "")).reduce((total, char) => {
    if (char === "\n") return total;
    return total + (char.charCodeAt(0) > 255 ? 1.8 : 1);
  }, 0);
}

function getWrappedLineCount(value, width) {
  const safeWidth = Math.max(Number(width) || 1, 1);
  const lines = String(value ?? "")
    .split(/\r?\n/)
    .map((line) => Math.max(1, Math.ceil(getExcelTextUnits(line) / safeWidth)));

  return Math.max(1, ...lines);
}

function getMergedColumnWidth(sheet, startCol, endCol) {
  let width = 0;

  for (let col = startCol; col <= endCol; col += 1) {
    width += Number(sheet.getColumn(col).width) || 8.43;
  }

  return width;
}

function adjustOverviewInfoRows(sheet, labels, values) {
  const labelWidth = Math.min(
    34,
    Math.max(
      Number(sheet.getColumn(1).width) || 16.625,
      ...[
        labels.contractName,
        labels.period,
        labels.contractor,
        labels.participants,
      ].map((label) => getExcelTextUnits(label) + 2),
    ),
  );

  sheet.getColumn(1).width = labelWidth;

  const valueWidth = Math.max(getMergedColumnWidth(sheet, 2, 8) - 4, 20);

  [
    [1, labels.contractName, values.contractName],
    [2, labels.period, values.period],
    [3, labels.contractor, values.contractor],
    [4, labels.participants, values.participants],
  ].forEach(([rowNumber, label, value]) => {
    const row = sheet.getRow(rowNumber);
    const labelLines = getWrappedLineCount(label, labelWidth - 1);
    const valueLines = getWrappedLineCount(value, valueWidth);
    const lineCount = Math.max(labelLines, valueLines, 1);

    row.height = Math.max(Number(row.height) || 36, 36 + (lineCount - 1) * 18);
  });
}

function getUd1000OverviewLabels(t) {
  return {
    contractName: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.overview.contractName",
      "1. 용역명칭 : ",
    ),
    period: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.overview.period",
      "2. 용역일자 : ",
    ),
    contractor: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.overview.contractor",
      "3. 용역업체 : ",
    ),
    participants: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.overview.participants",
      "4. 참여인원 : ",
    ),
    purpose: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.overview.purpose",
      "5. 진단목적",
    ),
    analysis: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.overview.analysis",
      "6. 진단결과 분석의견",
    ),
  };
}

function getUd1000IndexLabels(t) {
  return {
    title: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.index.title",
      "융합 점검 목록 및 결과",
    ),
    order: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.index.columns.order",
      "번호",
    ),
    facilityName: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.index.columns.facilityName",
      "설비명",
    ),
    inspection: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.index.columns.inspection",
      "점검",
    ),
    status: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.index.columns.status",
      "여부",
    ),
    result: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.index.columns.result",
      "결과",
    ),
    pageNumber: translateExcel(
      t,
      "projectDetail.projectReportEditor.ud1000Cover.index.columns.pageNumber",
      "페이지",
    ),
  };
}

function populateUd1000TemplatedIntroCoverSheet({ sheet, page, t }) {
  setCellValue(sheet, "A2", page?.projectName ?? "");
  setCellValue(sheet, "A7", page?.periodEndYearMonth ?? "");
  setCellValue(sheet, "A10", page?.contractor ?? "");
  setCellValue(
    sheet,
    "A4",
    page?.headline ||
      translateExcel(
        t,
        "projectDetail.projectReportEditor.ud1000Cover.introHeadline",
        "융복합 안전점검 결과보고서",
      ),
  );
}

function populateUd1000TemplatedOverviewCoverSheet({ sheet, page, t }) {
  const labels = getUd1000OverviewLabels(t);
  const values = {
    contractName: page?.contractName ?? "",
    period: getOverviewPeriodText(page),
    contractor: page?.contractor ?? "",
    participants: getOverviewParticipantText(page),
  };

  setCellValue(sheet, "A1", labels.contractName);
  setCellValue(sheet, "A2", labels.period);
  setCellValue(sheet, "A3", labels.contractor);
  setCellValue(sheet, "A4", labels.participants);
  setCellValue(sheet, "A6", labels.purpose);
  setCellValue(sheet, "A8", "");
  setCellValue(sheet, "A9", labels.analysis);
  setCellValue(sheet, "B1", values.contractName);
  setCellValue(sheet, "B2", values.period);
  setCellValue(sheet, "B3", values.contractor);
  setCellValue(sheet, "B4", values.participants);
  setCellValue(sheet, "A7", page?.purpose ?? "");
  setCellValue(sheet, "A10", getOverviewAnalysisText(page));
  adjustOverviewInfoRows(sheet, labels, values);
}

function populateUd1000TemplatedIndexCoverSheet({ sheet, page, t }) {
  const rows = padIndexRows(page?.rows, UD1000_INDEX_ROWS_PER_EXCEL_SHEET);
  const showCaption = page?.showIndexCaption !== false;
  const labels = getUd1000IndexLabels(t);

  if (showCaption) {
    setCellValue(sheet, "A1", page.title || labels.title);
  } else if (!showCaption) {
    setCellValue(sheet, "A1", "");
    sheet.getRow(1).hidden = true;
    sheet.getRow(2).hidden = true;
    sheet.pageSetup = {
      ...sheet.pageSetup,
      printArea: "A3:H18",
    };
  }

  setCellValue(sheet, "A3", labels.order);
  setCellValue(sheet, "B3", labels.facilityName);
  setCellValue(sheet, "F3", `${labels.inspection}\n${labels.status}`);
  setCellValue(sheet, "G3", `${labels.inspection}\n${labels.result}`);
  setCellValue(sheet, "H3", labels.pageNumber);

  rows.forEach((row, index) => {
    const rowNumber = 4 + index;
    const facilityDisplay = [row.facilityName, row.partName]
      .filter(Boolean)
      .join(" ");

    setCellValue(sheet, `A${rowNumber}`, row.order ?? "");
    setCellValue(sheet, `B${rowNumber}`, facilityDisplay);
    setCellValue(sheet, `F${rowNumber}`, row.inspectionStatus ?? "");
    setCellValue(sheet, `G${rowNumber}`, row.inspectionResult ?? "");
    setCellValue(sheet, `H${rowNumber}`, row.pageNumber ?? "");
  });
}

function populateUd1000TemplatedCoverSheet({ sheet, page, t }) {
  if (page?.coverPageType === "intro") {
    populateUd1000TemplatedIntroCoverSheet({ sheet, page, t });
    return;
  }

  if (page?.coverPageType === "overview") {
    populateUd1000TemplatedOverviewCoverSheet({ sheet, page, t });
    return;
  }

  populateUd1000TemplatedIndexCoverSheet({ sheet, page, t });
}

async function appendUd1000CoverSheets({
  workbook,
  coverPages,
  templateSheet,
  coverTemplateWorkbook,
  t,
  usedNames,
}) {
  if (
    !Array.isArray(coverPages) ||
    !coverPages.length ||
    (!templateSheet && !coverTemplateWorkbook)
  ) {
    return 0;
  }

  let logoBase64 = "";

  try {
    logoBase64 = await loadImageAsDataUrl(
      `${process.env.PUBLIC_URL || ""}/images/mainLogo.png`,
    );
  } catch (_error) {
    logoBase64 = "";
  }

  const embeddedCoverSheets = workbook === coverTemplateWorkbook;
  const embeddedSheetUsage = {
    intro: 0,
    overview: 0,
    index: 0,
  };

  for (const [index, coverPage] of coverPages.entries()) {
    const pageNumber = index + 1;
    const worksheetName = createWorksheetName(coverPage, index, usedNames);
    const coverTemplateSheet = getUd1000CoverTemplateSheet(
      coverTemplateWorkbook,
      coverPage?.coverPageType,
    );

    if (coverTemplateSheet) {
      const useEmbeddedSheet =
        embeddedCoverSheets &&
        embeddedSheetUsage[coverPage?.coverPageType] === 0;
      const sheet = useEmbeddedSheet
        ? coverTemplateSheet
        : workbook.addWorksheet(worksheetName);

      if (useEmbeddedSheet) {
        sheet.name = worksheetName;
        embeddedSheetUsage[coverPage?.coverPageType] += 1;
      } else {
        copyTemplateWorksheet(coverTemplateSheet, sheet, {
          sourceWorkbook: coverTemplateWorkbook,
          targetWorkbook: workbook,
          copyImages: true,
        });
      }

      applyProjectWorksheetPageSetup(sheet, pageNumber);
      populateUd1000TemplatedCoverSheet({
        sheet,
        page: coverPage,
        t,
      });
      reapplyTemplateWorksheetStyles(coverTemplateSheet, sheet);
      continue;
    }

    if (!templateSheet) {
      throw new Error("UD1000 cover template worksheet is unavailable");
    }

    const sheet = workbook.addWorksheet(worksheetName);

    initializeCoverWorksheet(sheet, templateSheet, pageNumber);

    if (coverPage?.coverPageType === "intro") {
      renderUd1000IntroCoverSheet({
        workbook,
        sheet,
        page: coverPage,
        logoBase64,
        t,
      });
      continue;
    }

    if (coverPage?.coverPageType === "overview") {
      renderUd1000OverviewCoverSheet({
        sheet,
        page: coverPage,
        t,
      });
      continue;
    }

    renderUd1000IndexCoverSheet({
      sheet,
      page: coverPage,
      t,
    });
  }

  return coverPages.length;
}

export async function downloadProjectUd1000ExcelReport({
  pages,
  coverPages = [],
  projectReportName,
  t,
  i18n,
  onProgress,
}) {
  const exportPages = Array.isArray(pages)
    ? pages.filter((page) => page?.file)
    : [];
  const exportCoverPages = expandUd1000ExcelCoverPages(
    Array.isArray(coverPages)
      ? coverPages.filter((page) => page?.isUd1000TemplateCover)
      : [],
    t,
  );
  const totalSheetCount = exportCoverPages.length + exportPages.length;

  if (!exportPages.length) {
    throw new Error(
      "downloadProjectUd1000ExcelReport requires at least one page",
    );
  }

  emitExportProgress(onProgress, {
    stage: "preparing",
    percent: 8,
    current: 0,
    total: totalSheetCount,
  });

  const needsImageTemplate = exportPages.some(
    (page) => page?.file?.file_type !== "video",
  );
  const needsVideoTemplate = exportPages.some(
    (page) => page?.file?.file_type === "video",
  );
  const templateWorkbook = needsImageTemplate
    ? await loadUdExcelTemplateWorkbook()
    : null;
  const templateSheet = templateWorkbook?.worksheets?.[0] || null;
  const videoTemplateWorkbook = needsVideoTemplate
    ? await loadUdVideoExcelTemplateWorkbook()
    : null;
  const videoTemplateSheet = videoTemplateWorkbook?.worksheets?.[0] || null;
  const coverTemplateSheet = templateSheet || videoTemplateSheet;
  const coverTemplateWorkbook = exportCoverPages.length
    ? await loadProjectUd1000CoverExcelTemplateWorkbook()
    : null;
  const workbook = coverTemplateWorkbook || new ExcelJS.Workbook();
  const usedNames = new Set();

  emitExportProgress(onProgress, {
    stage: "template-ready",
    percent: 18,
    current: 0,
    total: totalSheetCount,
  });

  const coverSheetCount = await appendUd1000CoverSheets({
    workbook,
    coverPages: exportCoverPages,
    templateSheet: coverTemplateSheet,
    coverTemplateWorkbook,
    t,
    usedNames,
  });

  if (coverSheetCount > 0) {
    emitExportProgress(onProgress, {
      stage: "worksheet",
      percent: getWorksheetProgressPercent(coverSheetCount, totalSheetCount),
      current: coverSheetCount,
      total: totalSheetCount,
    });
    await pauseForPaint();
  }

  for (const [index, page] of exportPages.entries()) {
    const sheet = workbook.addWorksheet(
      createWorksheetName(page, coverSheetCount + index, usedNames),
    );
    const images = await normalizeExcelImages(getUd1000ExcelImages(page.file));
    const pageNumber = coverSheetCount + index + 1;

    if (page?.file?.file_type === "video") {
      if (!videoTemplateSheet) {
        throw new Error("UD1000 video template worksheet is unavailable");
      }

      copyTemplateWorksheet(videoTemplateSheet, sheet);
      applyProjectWorksheetPageSetup(sheet, pageNumber);
      populateUdVideoExcelWorksheet({
        workbook,
        sheet,
        form: page.file,
        images,
        t,
        i18n,
      });
    } else {
      if (!templateSheet) {
        throw new Error("UD1000 image template worksheet is unavailable");
      }

      copyTemplateWorksheet(templateSheet, sheet);
      applyProjectWorksheetPageSetup(sheet, pageNumber);
      populateUdImageExcelWorksheet({
        workbook,
        sheet,
        form: page.file,
        images,
        t,
        i18n,
      });
    }

    emitExportProgress(onProgress, {
      stage: "worksheet",
      percent: getWorksheetProgressPercent(pageNumber, totalSheetCount),
      current: pageNumber,
      total: totalSheetCount,
    });

    await pauseForPaint();
  }

  const exportFileName =
    String(projectReportName || "project-report")
      .replace(/[\\/:*?"<>|]+/g, " ")
      .trim() || "project-report";

  emitExportProgress(onProgress, {
    stage: "packaging",
    percent: 92,
    current: totalSheetCount,
    total: totalSheetCount,
  });
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${exportFileName}.xlsx`);
  emitExportProgress(onProgress, {
    stage: "complete",
    percent: 100,
    current: totalSheetCount,
    total: totalSheetCount,
  });
}

export async function downloadProjectElectricalSafetyExcelReport({
  pages,
  projectReportName,
  currentUser = null,
  onProgress,
}) {
  const exportPages = Array.isArray(pages)
    ? pages.filter((page) => page?.file)
    : [];

  if (!exportPages.length) {
    throw new Error(
      "downloadProjectElectricalSafetyExcelReport requires at least one page",
    );
  }

  emitExportProgress(onProgress, {
    stage: "preparing",
    percent: 8,
    current: 0,
    total: exportPages.length,
  });

  const workbook = new ExcelJS.Workbook();
  const usedNames = new Set();
  const templateWorkbook = await loadElectricalSafetyExcelTemplateWorkbook();
  const templateSheet = templateWorkbook?.worksheets?.[0] || null;

  if (!templateSheet) {
    throw new Error("Electrical safety template worksheet is unavailable");
  }

  emitExportProgress(onProgress, {
    stage: "template-ready",
    percent: 18,
    current: 0,
    total: exportPages.length,
  });

  for (const [index, page] of exportPages.entries()) {
    const sheet = workbook.addWorksheet(
      createWorksheetName(page, index, usedNames),
    );
    const pageNumber = index + 1;

    copyTemplateWorksheet(templateSheet, sheet);
    applyProjectWorksheetPageSetup(sheet, pageNumber);
    await populateElectricalSafetyExcelWorksheet({
      workbook,
      sheet,
      file: page.file,
      images: getElectricalSafetyExcelImages(page.file),
      user: currentUser,
    });

    emitExportProgress(onProgress, {
      stage: "worksheet",
      percent: getWorksheetProgressPercent(pageNumber, exportPages.length),
      current: pageNumber,
      total: exportPages.length,
    });

    await pauseForPaint();
  }

  const exportFileName =
    String(projectReportName || "project-report")
      .replace(/[\\/:*?"<>|]+/g, " ")
      .trim() || "project-report";

  emitExportProgress(onProgress, {
    stage: "packaging",
    percent: 92,
    current: exportPages.length,
    total: exportPages.length,
  });
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${exportFileName}.xlsx`);
  emitExportProgress(onProgress, {
    stage: "complete",
    percent: 100,
    current: exportPages.length,
    total: exportPages.length,
  });
}
